import React, { useEffect, useMemo, useState } from "react"
import { Button, Card, CardBody, Col, Container, Row, Table } from "reactstrap"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { Pencil, Trash, Filter, FilterX, Lock } from "lucide-react"

import DeleteModal from "components/Common/DeleteModal"
import Spinners from "components/Shared/Spinners"
import NoDataAvailable from "components/Shared/NoDataAvailable"
import Paginations from "components/Common/Pagination"
import { handleStopPropagation } from "components/Common/Utils"
import Filters from "components/Shared/Filters"

import { showAlert } from "store/reducers/alertReducer"
import { deleteUser, getAllUsers } from "store/actions/userAction"
import {
  resetPayload,
  setPayload,
  toggleFilterVisibility,
} from "store/reducers/userReducer"
import { usePermissions } from "hooks"
import PasswordResetModal from "./components/PasswordResetModal"
import IconifyIcon from "components/Shared/IconifyIcon"
import AddPermissions from "./components/AddPermissions"
import { getStationShiftDropdownList } from "store/actions/commonAction"

const Users = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { permissions, showAll } = usePermissions("staff_users")
  const [showPasswordModal, setShowPasswordModal] = useState(false)
  const [selectedUser, setUser] = useState({})
  document.title = t("Users | Yamama")

  const filters = useMemo(() => {
    return {
      keyword: {
        type: "text",
        placeholder: t("Search by name,national id or phone number"),
        label: t("Search By"),
        colSize: 4,
      },
      order_by: {
        type: "select",
        placeholder: t("Select Order"),
        label: t("Select Order"),
        options: [
          { value: 1, label: t("Ascending") },
          { value: -1, label: t("Descending") },
        ],
        colSize: 4,
      },
      // status: {
      //   type: "select",
      //   placeholder: t("Filter by status"),
      //   label: t("Status"),
      //   options: [
      //     { label: t("Active"), value: "active" },
      //     { label: t("Inactive"), value: "inactive" },
      //   ],
      // },
    }
  }, [t])

  const dispatch = useDispatch()

  const {
    loading,
    isLoading,
    perPage,
    totalPages,
    totalCount,
    currentPage,
    users,
    payload,
    isShowFilter,
  } = useSelector(state => state.Users)

  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [trigger, setTrigger] = useState(false)
  const [showPermissionsModal, setShowPermissionsModal] = useState(false)

  useEffect(() => {
    getAllData()
  }, [trigger])

  useEffect(() => {
    dispatch(getStationShiftDropdownList())
  }, [])

  const getAllData = () => {
    dispatch(getAllUsers(payload))
  }

  const onPageChange = page => {
    dispatch(setPayload({ page_number: page }))
    setTrigger(!trigger)
  }

  const handleReset = () => {
    dispatch(resetPayload())
    setTrigger(!trigger)
  }

  const handleDelete = () => {
    dispatch(deleteUser(itemIdToDelete))
      .then(action => {
        const { statusCode, success, message, data } = action?.payload
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }))
          setTrigger(!trigger)
          setShowDeleteModal(false)
        } else {
          dispatch(showAlert({ text: message, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const showPermissionButton = permissions => {
    if (!permissions) return false

    if (!permissions.permissions) return false

    if (!Object.keys(permissions.permissions).includes("trial_run"))
      return false

    if (permissions.permissions["trial_run"].includes("can_mark_attendance"))
      return true
  }

  return (
    <React.Fragment>
      <div className="page-content">
        {loading ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <Col xl="12">
                <div className="d-flex justify-content-between mb-4 align-items-center">
                  <h4 className="card-title">{t("Users")}</h4>
                  <div className="d-flex gap-3">
                    {(showAll || permissions.includes("can_create")) && (
                      <button
                        className="main_btn"
                        onClick={() => navigate("/users/create")}
                      >
                        {t("Add New")}
                      </button>
                    )}
                    <Button
                      color="primary"
                      onClick={() => dispatch(toggleFilterVisibility())}
                      title={t("Show/Hide Filter")}
                    >
                      {isShowFilter ? <FilterX /> : <Filter />}
                    </Button>
                  </div>
                </div>
                {isShowFilter && (
                  <Filters
                    filters={filters}
                    state={{ ...payload.filters, order_by: payload.order_by }}
                    setState={data => {
                      const { order_by, ...rest } = data
                      dispatch(
                        setPayload({
                          order_by: Number(order_by),
                          filters: { ...payload.filters, ...rest },
                        })
                      )
                    }}
                    handleSearch={() => setTrigger(!trigger)}
                    handleReset={handleReset}
                    Replace
                    with
                    actual
                    translation
                    function
                  />
                )}
                <Card className="user_personal_info theme_card overflow-x-auto">
                  <CardBody>
                    {!loading && users.length === 0 ? (
                      <NoDataAvailable />
                    ) : (
                      <div className="table-responsive">
                        <Table className="table mb-0">
                          <thead className="table-light full_th">
                            <tr>
                              <th>{t("S. No.")}</th>
                              <th>{t("National Id")}</th>
                              <th>{t("Name")}</th>
                              <th>{t("Email")}</th>
                              <th>{t("Phone Number")}</th>
                              {/* <th>{t("User Type")}</th> */}
                              <th>{t("Role")}</th>
                              <th>{t("Action")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {users?.map((item, index) => (
                              <tr key={index}>
                                <th scope="row">
                                  {(currentPage - 1) * perPage + index + 1}
                                </th>
                                <td>{item?.national_id}</td>
                                <td>{item?.full_name}</td>
                                <td>{item?.email}</td>
                                <td>
                                  <p className="ltr fit_content">
                                    {item?.phone}
                                  </p>
                                </td>
                                {/* <td className="text-capitalize">
                                  {t(item?.user_type)}
                                </td> */}
                                <td className="text-capitalize">
                                  {item?.role}
                                </td>
                                <td onClick={handleStopPropagation}>
                                  <div className="d-flex gap-2 action_buttons">
                                    {(showAll ||
                                      permissions.includes("can_edit")) && (
                                      <Button
                                        color="secondary"
                                        onClick={() => {
                                          navigate(
                                            `/users/update?uuid=${item?.uuid}`
                                          )
                                        }}
                                        title={t("Update")}
                                      >
                                        <Pencil />
                                      </Button>
                                    )}
                                    {(showAll ||
                                      permissions.includes("can_delete")) && (
                                      <Button
                                        color="danger"
                                        onClick={() => {
                                          setItemIdToDelete(item.uuid)
                                          setShowDeleteModal(true)
                                        }}
                                        disabled={
                                          isLoading &&
                                          item?.uuid === itemIdToDelete
                                        }
                                        title={t("Delete")}
                                      >
                                        <Trash />
                                      </Button>
                                    )}
                                    {(showAll ||
                                      permissions.includes(
                                        "can_change_password"
                                      )) && (
                                      <Button
                                        color="primary"
                                        onClick={() => {
                                          setUser(item)
                                          setShowPasswordModal(true)
                                        }}
                                        disabled={
                                          isLoading &&
                                          item?.uuid === itemIdToDelete
                                        }
                                        title={t("Reset Password")}
                                      >
                                        <Lock />
                                      </Button>
                                    )}
                                    {showPermissionButton(
                                      item?.permissions
                                    ) && (
                                      <Button
                                        color="primary"
                                        onClick={() => {
                                          setUser(item)
                                          setShowPermissionsModal(true)
                                        }}
                                        title={t(
                                          "Trial Operation Station Permissions"
                                        )}
                                      >
                                        <IconifyIcon
                                          icon="fluent-mdl2:permissions-solid"
                                          className="text-white"
                                        />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    )}
                  </CardBody>
                </Card>
                {!loading && users.length > 0 && (
                  <Paginations
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    onPageChange={onPageChange}
                  />
                )}
              </Col>
            </Row>
          </Container>
        )}
      </div>
      <DeleteModal
        show={showDeleteModal}
        onDeleteClick={handleDelete}
        onCloseClick={() => {
          setItemIdToDelete(null)
          setShowDeleteModal(false)
        }}
        isLoading={isLoading}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this User?")}
      />
      {showPasswordModal && (
        <PasswordResetModal
          isOpen={showPasswordModal}
          toggle={() => setShowPasswordModal(!showPasswordModal)}
          user={selectedUser}
        />
      )}
      {showPermissionsModal && selectedUser && (
        <AddPermissions
          isOpen={showPermissionsModal}
          toggle={() => {
            setUser({})
            setShowPermissionsModal(false)
          }}
          user_uuid={selectedUser?.uuid}
        />
      )}
    </React.Fragment>
  )
}

export default Users
